import React, { useState } from 'react';
import { FileText, Eye } from 'lucide-react';
import { DOC_LABELS, UPLOADABLE_DOC_KEYS, docLabel } from '../lib/docLabels';

/** Shared documents manager — view existing docs and (when allowed) upload
 *  or replace them. URL builders are injected so recruiter and student
 *  portals can reuse the same component. */
export default function DocManager({ documents, canUpload, buildViewUrl, buildUploadUrl, onChanged, testIdPrefix = 'docs' }) {
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState('');
  const docs = documents || {};
  const existingKeys = Object.keys(docs).filter((k) => docs[k]);
  const missingKeys = canUpload ? UPLOADABLE_DOC_KEYS.filter((k) => !docs[k]) : [];

  const view = async (key) => {
    try {
      const res = await fetch(buildViewUrl(key), { credentials: 'include' });
      if (!res.ok) { setError('Could not open document'); return; }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { setError('Could not open document'); }
  };

  const upload = async (key, file) => {
    setError('');
    if (file.size > 10 * 1024 * 1024) { setError('File exceeds 10 MB limit'); return; }
    setBusyKey(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(buildUploadUrl(key), { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Upload failed');
        return;
      }
      onChanged?.(key);
    } finally {
      setBusyKey(null);
    }
  };

  const UploadBtn = ({ docKey, label }) => (
    <label
      className={`shrink-0 text-[11px] px-2 py-1 rounded-md border transition ${
        busyKey === docKey ? 'opacity-60 cursor-wait bg-white border-tge-navy/10' : 'cursor-pointer bg-white border-tge-navy/20 hover:border-tge-navy/50 text-tge-navy'
      }`}
      data-testid={`${testIdPrefix}-upload-btn-${docKey}`}
    >
      {busyKey === docKey ? 'Uploading…' : label}
      <input
        type="file"
        className="hidden"
        disabled={busyKey === docKey}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) upload(docKey, f);
        }}
        data-testid={`${testIdPrefix}-upload-input-${docKey}`}
      />
    </label>
  );

  return (
    <div data-testid={`${testIdPrefix}-manager`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {existingKeys.map((key) => (
          <div key={key} className="flex items-center gap-2 border border-tge-navy/10 rounded-lg bg-white pr-2" data-testid={`${testIdPrefix}-row-${key}`}>
            <button
              type="button"
              onClick={() => view(key)}
              className="flex-1 flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-tge-cream-soft/40 rounded-l-lg min-w-0"
              data-testid={`${testIdPrefix}-view-${key}`}
            >
              <FileText size={13} className="opacity-70 shrink-0" />
              <span className="truncate">{docLabel(key)}</span>
              <Eye size={12} className="ml-auto opacity-40 shrink-0" />
            </button>
            {canUpload && <UploadBtn docKey={key} label="Replace" />}
          </div>
        ))}
        {existingKeys.length === 0 && (
          <div className="text-xs text-tge-muted italic col-span-2">No documents on file yet.</div>
        )}
      </div>

      {missingKeys.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wider text-tge-muted font-semibold mb-1.5">Add more documents</div>
          <div className="flex flex-wrap gap-2">
            {missingKeys.map((key) => (
              <div key={key} className="flex items-center gap-1.5 border border-dashed border-tge-navy/20 rounded-lg px-2 py-1 bg-tge-cream-soft/30">
                <span className="text-[11px] text-tge-navy/60">{DOC_LABELS[key]}</span>
                <UploadBtn docKey={key} label="Upload" />
              </div>
            ))}
          </div>
        </div>
      )}
      {error && <div className="text-xs text-rose-600 mt-2" data-testid={`${testIdPrefix}-error`}>{error}</div>}
    </div>
  );
}
